import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const AdmissionApplicationProcedureSection = () => {
    const { school_slug } = useParams();

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* ---------------- SECTION STATE ---------------- */
    const [form, setForm] = useState({
        heading: "",
        intro_paragraph: "",
        manual_title: "",
        manual_content: "",
        online_title: "",
        online_content: "",
        withdrawal_title: "",
        withdrawal_content: "",
    });

    /* ---------------- LIST STATES ---------------- */
    const [listCards, setListCards] = useState([
        {
            title: "Important Notes",
            type: "important_notes",
            items: [""],
        },
        {
            title: "Documents Required",
            type: "documents_required",
            items: [""],
        },
        {
            title: "Refund Policy",
            type: "refund_policy",
            items: [""],
        },
    ]);


    /* ---------------- Images --------------------- */
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState("");

    /* ---------------- FETCH DATA ---------------- */
    useEffect(() => {
        fetchAdmissionProcedure();
    }, []);

    const fetchAdmissionProcedure = async () => {
        try {
            const res = await axios.get(
                `${baseAPIUrl}admin/admission_page/get_admission_procedure_section/${school_slug}`,
                { withCredentials: true }
            );

            if (res.data.status === 1 && res.data.data) {
                const { section, lists } = res.data.data;

                setForm({
                    heading: section?.heading || "",
                    intro_paragraph: section?.intro_paragraph || "",
                    manual_title: section?.manual_title || "",
                    manual_content: section?.manual_content || "",
                    online_title: section?.online_title || "",
                    online_content: section?.online_content || "",
                    withdrawal_title: section?.withdrawal_title || "",
                    withdrawal_content: section?.withdrawal_content || "",
                });

                if (lists && lists.length) {
                    const grouped = {};

                    lists.forEach(item => {
                        if (!grouped[item.list_type]) {
                            grouped[item.list_type] = {
                                title: item.title,
                                type: item.list_type,
                                items: [],
                            };
                        }
                        grouped[item.list_type].items.push(item.content);
                    });

                    setListCards(Object.values(grouped));
                }

                if (section?.image) {
                    setImagePreview(
                        `${baseAPIUrl}assets/images/admission/${section.image}`
                    );
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- INPUT HANDLERS ---------------- */
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleCardTitleChange = (index, value) => {
        const updated = [...listCards];
        updated[index].title = value;
        setListCards(updated);
    };


    const handleItemChange = (cardIndex, itemIndex, value) => {
        const updated = [...listCards];
        updated[cardIndex].items[itemIndex] = value;
        setListCards(updated);
    };


    const addItem = (cardIndex) => {
        const updated = [...listCards];
        updated[cardIndex].items.push("");
        setListCards(updated);
    };

    const deleteItem = (cardIndex, itemIndex) => {
        const updated = [...listCards];
        if (updated[cardIndex].items.length <= 1) return;
        updated[cardIndex].items.splice(itemIndex, 1);
        setListCards(updated);
    };


    const addCard = () => {
        setListCards([
            ...listCards,
            {
                title: "New List",
                type: `custom_${Date.now()}`,
                items: [""],
            },
        ]);
    };


    const deleteCard = (cardIndex) => {
        if (listCards.length <= 1) return;
        setListCards(listCards.filter((_, i) => i !== cardIndex));
    };


    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview.startsWith("blob:")) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    /* ---------------- SUBMIT ---------------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("school_slug", school_slug);
            formData.append("section", JSON.stringify(form));

            if (image) {
                formData.append("image", image);
            }

            const listsPayload = [];

            listCards.forEach(card => {
                card.items.forEach((item, index) => {
                    listsPayload.push({
                        list_type: card.type,
                        title: card.title,
                        content: item,
                        sort_order: index + 1,
                    });
                });
            });

            formData.append("lists", JSON.stringify(listsPayload));

            const res = await axios.post(
                `${baseAPIUrl}admin/admission_page/add_edit_admission_procedure_section`,
                formData,
                { withCredentials: true }
            );

            setMessage(res.data.msg);
            scrollToTop();
        } catch (err) {
            console.error(err);
            setMessage("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <p>Loading...</p>;

    return (
        <div className="content-wrapper">
            {/* Header */}
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 text-dark">
                                Admission Application Procedure
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item">
                                    <a href="/admin/dashboard">Dashboard</a>
                                </li>
                                <li className="breadcrumb-item active">Admission page</li>
                                <li className="breadcrumb-item active">
                                    Admission Procedure
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <section className="content">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-header">
                            <h4>Edit Admission Application Procedure</h4>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="card-body">
                                {message && (
                                    <p className="text-center text-danger">{message}</p>
                                )}

                                {/* Section Fields */}
                                <div className="form-group">
                                    <label>Heading</label>
                                    <input
                                        type="text"
                                        name="heading"
                                        className="form-control"
                                        value={form.heading}
                                        placeholder="Heading"
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Intro Paragraph</label>
                                    <textarea
                                        name="intro_paragraph"
                                        rows="4"
                                        className="form-control"
                                        value={form.intro_paragraph}
                                        placeholder="Intro paragraph"
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Manual */}
                                <div className="form-group">
                                    <label>Manual Submission Title</label>
                                    <input
                                        type="text"
                                        name="manual_title"
                                        className="form-control"
                                        value={form.manual_title}
                                        placeholder="Manual submission title"
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Manual Submission Content</label>
                                    <textarea
                                        name="manual_content"
                                        rows="4"
                                        className="form-control"
                                        value={form.manual_content}
                                        placeholder="Mannual submission content"
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Online */}
                                <div className="form-group">
                                    <label>Online Submission Title</label>
                                    <input
                                        type="text"
                                        name="online_title"
                                        className="form-control"
                                        value={form.online_title}
                                        placeholder="Online submission title"
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Online Submission Content</label>
                                    <textarea
                                        name="online_content"
                                        rows="4"
                                        className="form-control"
                                        value={form.online_content}
                                        placeholder="Online submission title"
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* LIST BLOCKS */}
                                {listCards.map((card, cardIndex) => (
                                    <div className="card card-outline card-danger mb-4" key={cardIndex}>
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm w-75"
                                                value={card.title}
                                                onChange={(e) =>
                                                    handleCardTitleChange(cardIndex, e.target.value)
                                                }
                                            />

                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => deleteCard(cardIndex)}
                                                disabled={listCards.length === 1}
                                            >
                                                ✕
                                            </button>
                                        </div>

                                        <div className="card-body">
                                            {card.items.map((item, itemIndex) => (
                                                <div
                                                    key={itemIndex}
                                                    className="d-flex gap-2 mb-2"
                                                >
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={item}
                                                        onChange={(e) =>
                                                            handleItemChange(cardIndex, itemIndex, e.target.value)
                                                        }
                                                    />

                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() =>
                                                            deleteItem(cardIndex, itemIndex)
                                                        }
                                                        disabled={card.items.length === 1}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}

                                            <button
                                                type="button"
                                                className="btn btn-success btn-sm mt-2"
                                                onClick={() => addItem(cardIndex)}
                                            >
                                                + Add Item
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    className="btn btn-primary mb-3"
                                    onClick={addCard}
                                >
                                    + Add New Card
                                </button>


                                {/* Withdrawal */}
                                <div className="form-group">
                                    <label>Withdrawal Title</label>
                                    <input
                                        type="text"
                                        name="withdrawal_title"
                                        className="form-control"
                                        value={form.withdrawal_title}
                                        placeholder="Withdrawal title"
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Withdrawal Content</label>
                                    <textarea
                                        name="withdrawal_content"
                                        rows="3"
                                        className="form-control"
                                        placeholder="Withdrawal Content"
                                        value={form.withdrawal_content}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Image Upload */}
                                <div className="form-group">
                                    <label>Admission Image</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setImage(file);
                                                setImagePreview(URL.createObjectURL(file));
                                            }
                                        }}
                                    />

                                    {imagePreview && (
                                        <div style={{ marginTop: "10px" }}>
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                style={{
                                                    maxWidth: "120px",
                                                    borderRadius: "8px",
                                                    border: "1px solid #ccc",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                            </div>

                            <div className="card-footer">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AdmissionApplicationProcedureSection;
